import React, { useState, useRef, useEffect, useCallback } from 'react';
import DashboardLayout from '../../../../layouts/DashboardLayout';
import { aiAssistantApi } from '../../api/aiAssistant.api';
import { usePrivileges } from '../../../../hooks/usePrivileges';
import { useChatHistory } from '../../../../hooks/useChatHistory';
import PanelControlIA from './AiControlPanel';
import {
  FaRobot,
  FaPaperPlane,
  FaSpinner,
  FaSyncAlt,
  FaTrashAlt,
  FaChevronDown,
  FaCheckDouble,
  FaCheck,
  FaUser,
} from 'react-icons/fa';
import styles from '../../styles/AiAssistant.module.css';
import { TABS_AI_ASSISTANT, ENUM_TABS_AI_ASSISTANT } from '../../interfaces/aiAssistan.interface';
import { ENUM_ROLES } from '../../../../interaces/enums/enums.interface';
import { SUGGESTIONS } from '../../interfaces/aiAssistan.interface';
import { hasRole } from '../../../../services/helpers';
import { ENUM_MESSAGES_ROLES } from '../../interfaces/aiAssistan.interface';
function formatHour(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function dayLabel(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (sameDay(d, today)) return 'Hoy';
    if (sameDay(d, yesterday)) return 'Ayer';

    return d.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'long',
      year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return '';
  }
}

function dayKey(iso) {
  if (!iso) return 'unknown';
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  } catch {
    return 'unknown';
  }
}

function IaAssistant() {
  const { role } = usePrivileges();
  const [activeTab, setActiveTab] = useState(ENUM_TABS_AI_ASSISTANT.CHAT);
  const { messages, addMessage, updateMessage, clearHistory, username } = useChatHistory();

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false); // indicador "escribiendo..."
  const [ingestLoading, setIngestLoading] = useState(false);
  const [ingestMsg, setIngestMsg] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const chatEndRef = useRef(null);
  const chatWinRef = useRef(null);
  const textareaRef = useRef(null);

  /* ── Auto-scroll al fondo ── */
  const scrollToBottom = useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  /* ── Botón "bajar" cuando el usuario sube en el historial ── */
  useEffect(() => {
    const el = chatWinRef.current;
    if (!el) return;
    const handleScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowScrollBtn(distFromBottom > 120);
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  /* ── Auto-resize del textarea ── */
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [input]);

  /* ── Enviar mensaje ── */
  const sendMessage = async (question = input) => {
    if (!question.trim() || loading) return;
    const userMsg = {
      id: Date.now(),
      role: ENUM_MESSAGES_ROLES.USER,
      content: question,
      timestamp: new Date().toISOString(),
      status: 'sent',
    };
    addMessage(userMsg);
    setInput('');
    setLoading(true);
    setIsTyping(true);

    const assistantId = Date.now() + 1;

    try {
      const response = await aiAssistantApi.chat(question);
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let assistantCreated = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') break;

          let token = '';
          try {
            const parsed = JSON.parse(data);
            token = parsed.data ?? parsed ?? '';
          } catch {
            token = data;
          }

          if (!assistantCreated) {
            setIsTyping(false);
            addMessage({
              id: assistantId,
              role: 'assistant',
              content: token,
              streaming: true,
              timestamp: new Date().toISOString(),
            });
            assistantCreated = true;
          } else {
            updateMessage(assistantId, (m) => ({
              content: m.content + token,
            }));
          }
        }
      }

      // Si no llegó ningún chunk, crear el mensaje vacío igualmente
      if (!assistantCreated) {
        setIsTyping(false);
        addMessage({
          id: assistantId,
          role: 'assistant',
          content: 'Sin respuesta del servidor.',
          streaming: false,
          timestamp: new Date().toISOString(),
        });
      } else {
        updateMessage(assistantId, () => ({ streaming: false }));
      }

      updateMessage(userMsg.id, () => ({ status: 'read' }));
    } catch (err) {
      setIsTyping(false);
      addMessage({
        id: assistantId,
        role: 'assistant',
        content: `${err.message}`,
        streaming: false,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleIngest = async () => {
    setIngestLoading(true);
    setIngestMsg(null);
    try {
      const res = await aiAssistantApi.triggerIngest();
      setIngestMsg(`Ingesta completada: ${res.chunks_indexed} registros indexados.`);
    } catch (err) {
      setIngestMsg(`${err.message}`);
    } finally {
      setIngestLoading(false);
    }
  };

  /* ── Ticks de mensaje tipo WA ── */
  const renderTicks = (msg) => {
    if (msg.role !== ENUM_MESSAGES_ROLES.USER) return null;
    if (msg.status === 'read')
      return <FaCheckDouble className={`${styles.msgTicks} ${styles.msgTicksRead}`} />;
    if (msg.status === 'delivered') return <FaCheckDouble className={styles.msgTicks} />;
    return <FaCheck className={styles.msgTicks} />;
  };

  const isGrouped = (msgs, idx) => {
    if (idx === 0) return false;
    return msgs[idx].role === msgs[idx - 1].role;
  };

  /* ── Renderizar separadores de fecha ── */
  const needsDateSep = (msgs, idx) => {
    if (idx === 0) return true;
    return dayKey(msgs[idx].timestamp) !== dayKey(msgs[idx - 1].timestamp);
  };

  return (
    <DashboardLayout>
      <div className="page-container">
        {/* ── Tabs SUPERADMIN ── */}
        {hasRole([ENUM_ROLES.GOVERNMENT_OFFICIAL, ENUM_ROLES.SUPERADMIN], role) && (
          <div className="tabs-bar">
            {TABS_AI_ASSISTANT.map((tab) => (
              <>
                {hasRole(tab.roles, role) && (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`tab-item ${activeTab === tab.id ? 'tab-item-active' : ''}`}
                  >
                    {tab.icon} {tab.id}
                  </button>
                )}
              </>
            ))}
          </div>
        )}

        {/* ── Panel de Control ── */}
        {activeTab === ENUM_TABS_AI_ASSISTANT.PANEL_DE_CONTROL && hasRole([ENUM_ROLES.GOVERNMENT_OFFICIAL, ENUM_ROLES.SUPERADMIN], role)&&(
            <div className={styles.panelWrapper}>
              <PanelControlIA />
            </div>
          )}

        {activeTab === ENUM_TABS_AI_ASSISTANT.CHAT && hasRole([ENUM_ROLES.GOVERNMENT_OFFICIAL, ENUM_ROLES.SUPERADMIN], role) && (
          <div className={styles.waPage}>
            {/* ── Barra superior ── */}
            <div className={styles.waHeader}>
              <div className={styles.waHeaderLeft}>
                <div className={styles.waAvatar}>
                  <FaRobot />
                </div>
                <div className={styles.waHeaderInfo}>
                  <h3>Asistente IA</h3>
                  <span className={styles.waStatus}>
                    {isTyping ? (
                      <span className={styles.waStatusTyping}>escribiendo…</span>
                    ) : (
                      <>
                        <span className={styles.waStatusDot} />
                        en línea
                      </>
                    )}
                  </span>
                </div>
              </div>

              <div className={styles.waHeaderRight}>
                {/* Re-indexar — solo SUPERADMIN */}
                {hasRole([ENUM_ROLES.SUPERADMIN], role) && (
                  <button
                    className={styles.waHeaderBtn}
                    onClick={handleIngest}
                    disabled={ingestLoading}
                    title="Re-indexar datos"
                  >
                    {ingestLoading ? (
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <FaSyncAlt />
                    )}
                  </button>
                )}
                {/* Limpiar chat */}
                <button
                  className={styles.waHeaderBtn}
                  onClick={() => setConfirmClear(true)}
                  title="Limpiar conversación"
                >
                  <FaTrashAlt />
                </button>
              </div>
            </div>

            {/* ── Notificación de ingesta ── */}
            {ingestMsg && (
              <div
                className={`${styles.ingestMsg} ${ingestMsg.startsWith('Ok') ? styles.ingestOk : styles.ingestError}`}
              >
                {ingestMsg}
              </div>
            )}

            {/* ── Cuerpo scrollable ── */}
            <div className={styles.waBody}>
              <div className={styles.waChatWindow} ref={chatWinRef}>
                {/* Sugerencias (solo mensaje de bienvenida) */}
                {messages.length === 1 && (
                  <div className={styles.suggestionsWrap}>
                    <p className={styles.suggestionsLabel}>Preguntas frecuentes:</p>
                    <div className={styles.suggestionsRow}>
                      {SUGGESTIONS.map((s, i) => (
                        <button
                          key={i}
                          className={styles.suggestionBtn}
                          onClick={() => sendMessage(s)}
                          disabled={loading}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mensajes */}
                {messages.map((msg, idx) => (
                  <React.Fragment key={msg.id}>
                    {/* Separador de fecha */}
                    {needsDateSep(messages, idx) && (
                      <div className={styles.dateSeparator}>
                        <span className={styles.dateSeparatorChip}>{dayLabel(msg.timestamp)}</span>
                      </div>
                    )}

                    {/* Fila del mensaje */}
                    <div
                      className={`${styles.msgRow} ${
                        msg.role === ENUM_MESSAGES_ROLES.USER ? styles.msgRowUser : styles.msgRowAssistant
                      } ${isGrouped(messages, idx) ? styles.grouped : ''}`}
                    >
                      {/* Mini avatar */}
                      <div
                        className={`${styles.msgMiniAvatar} ${
                          msg.role === ENUM_MESSAGES_ROLES.USER
                            ? styles.msgMiniAvatarUser
                            : styles.msgMiniAvatarAssistant
                        }`}
                      >
                        {msg.role === ENUM_MESSAGES_ROLES.USER ? <FaUser /> : <FaRobot />}
                      </div>

                      {/* Burbuja */}
                      <div
                        className={`${styles.msgBubble} ${
                          msg.role === ENUM_MESSAGES_ROLES.USER ? styles.msgBubbleUser : styles.msgBubbleAssistant
                        }`}
                      >
                        {msg.content || (msg.streaming && '')}

                        {/* Meta: hora + ticks */}
                        <div className={styles.msgMeta}>
                          <span className={styles.msgTime}>{formatHour(msg.timestamp)}</span>
                          {renderTicks(msg)}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ))}

                {/* Indicador "escribiendo..." animado */}
                {isTyping && (
                  <div className={styles.typingRow}>
                    <div className={`${styles.msgMiniAvatar} ${styles.msgMiniAvatarAssistant}`}>
                      <FaRobot />
                    </div>
                    <div className={styles.typingBubble}>
                      <span className={styles.typingDot} />
                      <span className={styles.typingDot} />
                      <span className={styles.typingDot} />
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Botón bajar al fondo */}
              {showScrollBtn && (
                <button
                  className={styles.scrollDownBtn}
                  onClick={scrollToBottom}
                  title="Ir al final"
                >
                  <FaChevronDown />
                </button>
              )}
            </div>

            {/* ── Barra de entrada ── */}
            <div className={styles.waInputBar}>
              <div className={styles.waTextareaWrap}>
                <textarea
                  ref={textareaRef}
                  className={styles.waTextarea}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Escribe un mensaje…"
                  rows={1}
                  disabled={loading}
                />
              </div>
              <button
                className={styles.waSendBtn}
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                title="Enviar"
              >
                {loading ? (
                  <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <FaPaperPlane />
                )}
              </button>
            </div>
          </div>
        )}

        {/* ── Modal confirmación limpiar ── */}
        {confirmClear && (
          <div className={styles.confirmOverlay}>
            <div className={styles.confirmBox}>
              <FaTrashAlt className={styles.confirmIcon} />
              <p className={styles.confirmText}>
                ¿Deseas borrar todo el historial de esta conversación? Esta acción no se puede
                deshacer.
              </p>
              <div className={styles.confirmActions}>
                <button className="btn-secondary" onClick={() => setConfirmClear(false)}>
                  Cancelar
                </button>
                <button
                  className={styles.confirmDelete}
                  onClick={() => {
                    clearHistory();
                    setConfirmClear(false);
                    setIngestMsg(null);
                  }}
                >
                  Sí, limpiar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default IaAssistant;
